import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ComponentManager from '@/Management/ComponentManager';
import ButtonViewModel from '@/ViewModels/Controls/ButtonViewModel';
import ButtonViewModelBuilder from '@/Builder/ButtonViewModelBuilder';
import CheckBoxViewModel from '@/ViewModels/Controls/CheckBoxViewModel';
import CheckBoxViewModelBuilder from '@/ViewModels/Controls/Builder/CheckBoxViewModelBuilder';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { IOperationResultDTO } from "coreframetypings/dto";
import RADApplication from '@/Management/RADApplication';
import Utilities from '@/Utility/Utilities';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import ResourceManager from '@/Management/ResourceManager';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import ViewViewModel from '@/ViewModels/ViewViewModel';
import {ITwoFactorInitialConfigViewModelConstructorParameter} from '#/DefaultViewExtensionInterfaces';

export default class TwoFactorInitialConfigViewModel extends ViewViewModel {

    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    public token: KnockoutObservable<string>;
    public relogButton: ButtonViewModel;
    public cancelButton: ButtonViewModel;
    public acceptCheckbox: CheckBoxViewModel;
    public headerLabel: LabelViewModel;
    public usernameLabel: LabelViewModel;
    public instructionLabel: LabelViewModel;
    
    public callForGenerateToken: KnockoutObservable<boolean>;
    
    public interactionCallback: () => void;

    public constructor(protected params: ITwoFactorInitialConfigViewModelConstructorParameter) {
        super(params);

        this.token = this.observableOrDefault(null);

        this.callForGenerateToken = this.observableOrDefault(params.callForGenerateToken, false);

        this.acceptCheckbox = this.createAcceptCheckbox();
        this.relogButton = this.createRelogButton();
        this.cancelButton = this.createCancelButton();

        this.headerLabel = this.createHeaderLabel();
        this.usernameLabel = this.createUserLabel();
        this.instructionLabel = this.createInstructionLabel();
        
        this.interactionCallback = this.handlerOrDefault(params.interactionCallback);

        this.generateToken();
    }
    
    private createHeaderLabel(): LabelViewModel {
        return new LabelViewModelBuilder().withErgName('RAD.RADFOUNDATION.PROBLEMDOMAIN.USER_QR_TITLE_INSTRUCTIONS_APP')
            .build();
    }

    private createInstructionLabel(): LabelViewModel {
        return new LabelViewModelBuilder().withErgName('RAD.RADFOUNDATION.PROBLEMDOMAIN.USER_QR_INSTRUCTIONS_APP')
            .withSimpleReplacement('appname', RADApplication.getInstance().appName())
            .build();
    }

    private createRelogButton(): ButtonViewModel {
        return new ButtonViewModelBuilder()
            .withParentLocator(this.locator)
            .withLocator('initialconfig.relog')
            .withOnClick(() => {
                this.activateTwoFactorAuth().then(() => {
                    return RADEnvironmentProxy.getInstance().logout();
                }).then(() => {
                    this.interactionCallback();
                });
            })
            .withIsEnabled(false)
            .withLocator(LocatorHelper.buildLocator('twofa', 'relogin'))
            .withLabel().withErgName('TWO_FA.RELOG').done()
            .build()
    }

    private createAcceptCheckbox(): CheckBoxViewModel {
        return new CheckBoxViewModelBuilder()
            .withLocator(this.locator() + '.initialconfig.accept-checkbox')
            .withLabel(this.LabelViewModel.create('RAD.RADFOUNDATION.PROBLEMDOMAIN.USER_QR_ACCEPT_CHECKBOX'))
            .withIsCheckedSubscription(this.onAcceptChecked.bind(this))
            .withLocator(LocatorHelper.buildLocator('twofa', 'registered'))
            .create();
    }

    private createCancelButton(): ButtonViewModel {
        return new ButtonViewModelBuilder()
            .withParentLocator(this.locator)
            .withLocator('initialconfig.cancel')
            .withLabel().withErgName('CANCEL').done()
            .withOnClick(() => {
                RADEnvironmentProxy.getInstance().logout().then(() => {
                    this.interactionCallback();
                });
            })
            .build();
    }

    /**
     * @override
     */
    protected createUserLabel(): LabelViewModel {
        const prefix = ResourceManager.getInstance().get('USER_NAME');

        return new LabelViewModelBuilder()
            .withCaption(`${prefix}: ${RADApplication.getInstance().user.username()}`)
            .build();
    }

    private activateTwoFactorAuth(): JQueryPromise<IOperationResultDTO> {
        return RADEnvironmentProxy.getInstance().callStaticOperation({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.User',
            operationName: 'ActivateTwoFactorInitialConfig',
            operationParameters: [],
            contextParameters: {}
        }).promise();
    }

    public generateToken(): void {
        this.callGenerateToken()
            .then((result) => {
                if (this.validateStatus(result, false)) {
                    const token = result.returnValue;
                    if (!Utilities.isNullOrUndefinedOrBlank(token)) {
                        this.token(token);
                    }
                }
                else {
                    const errorMessage = MessageBoxViewModel.createErrorMessageBox(
                        result.errorMessage,
                        null,
                        null,
                        null,
                        null, 
                        {
                            onClose: () => {
                                return RADEnvironmentProxy.getInstance().logout().then(
                                    () => RADApplication.getInstance().afterLogout())
                            }
                        }
                    );
                    RADApplication.getInstance().showMessageBox(errorMessage);
                }
            });
    }

    protected callGenerateToken(): JQueryPromise<IOperationResultDTO> {
        return RADEnvironmentProxy.getInstance().callStaticOperation({
            pdTypeName: 'RAD.RADFoundation.ProblemDomain.User',
            operationName: 'GenerateTwoFactorInitialConfigToken',
            operationParameters: [],
            contextParameters: {}
        }).promise();
    }

    private onAcceptChecked(isChecked: boolean): void {
        this.relogButton.isEnabled(isChecked);
    }
}

ComponentManager.setViewModel('TwoFactorInitialConfigViewModel', TwoFactorInitialConfigViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');